import axios from 'axios'
import { readCookie } from './index'

// 特典付与代行リスト 1エントリ
export type IncentiveEntry = {
  name: string
  email: string
  role: '紹介' | '友人'
  count: number
}

// 集約APIのレスポンス
export type AggregateResponse = {
  entries: IncentiveEntry[]
  unmatched: { mail?: string; code?: string; reason: string }[]
  skipped: { tokey: string; role: string; reason: string }[]
  matched_tokeys: string[]
  // 対象CV(tokey単位)の行データ（CV一覧と同形式）。期間フィルタ非依存
  cv_rows: Record<string, any>[]
  // tokey => CSVで紐づいたメール（cv側に無い場合のフォールバック）
  csv_friend_emails: Record<string, string>
}

// 共通のAuthorizationヘッダ
const authHeaders = () => ({
  Authorization: `Bearer ${readCookie('access_token')}`,
})

// tokey指定で集約
// csvFriendEmails: 初回CSVアップロード時のフォールバックメール（tokey → email）
export async function aggregateByTokeys(
  clientId: number,
  tokeys: string[],
  csvFriendEmails?: Record<string, string>,
): Promise<AggregateResponse> {
  const response = await axios.post(
    `/admin/api/clients/${clientId}/incentive-lists/aggregate`,
    {
      tokeys,
      csv_friend_emails: csvFriendEmails || {},
    },
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    },
  )
  return response.data
}

// CSVアップロードで集約
export async function aggregateByCsv(
  clientId: number,
  file: File,
): Promise<AggregateResponse> {
  const formData = new FormData()
  formData.append('csv', file)
  const response = await axios.post(
    `/admin/api/clients/${clientId}/incentive-lists/aggregate`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...authHeaders(),
      },
    },
  )
  return response.data
}

// 選択済みエントリをExcelエクスポート
export async function exportIncentiveListExcel(
  clientId: number,
  entries: IncentiveEntry[],
): Promise<Blob> {
  const response = await axios.post(
    `/admin/api/clients/${clientId}/incentive-lists/export`,
    { entries },
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      responseType: 'blob',
    },
  )
  return response.data as Blob
}

// チェック済みtokeyを発行済み(gave_incentive=true)にする
export async function confirmIncentiveList(
  clientId: number,
  tokeys: string[],
): Promise<{ confirmed: number }> {
  const response = await axios.post(
    `/admin/api/clients/${clientId}/incentive-lists/confirm`,
    { tokeys },
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    },
  )
  return response.data
}

// 保存済み特典付与対象者リスト 1件
export type SavedIncentiveList = {
  id: number
  name: string
  entry_count: number
  created_at: string
}

// entriesスナップショットを保存（Excelダウンロード時の自動保存に使う）
export async function saveIncentiveList(
  clientId: number,
  entries: IncentiveEntry[],
): Promise<SavedIncentiveList> {
  const response = await axios.post(
    `/admin/api/clients/${clientId}/incentive-lists/saved`,
    { entries },
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    },
  )
  return response.data
}

// 保存済みリスト1件（対象者entries付き）
export type SavedIncentiveListDetail = SavedIncentiveList & {
  entries: IncentiveEntry[]
}

// 保存済みリストの詳細（対象者entries含む）を取得
export async function fetchSavedIncentiveList(
  clientId: number,
  id: number,
): Promise<SavedIncentiveListDetail> {
  const response = await axios.get(
    `/admin/api/clients/${clientId}/incentive-lists/saved/${id}`,
    { headers: authHeaders() },
  )
  return response.data
}

// 保存済みリスト名を変更
export async function updateSavedIncentiveListName(
  clientId: number,
  id: number,
  name: string,
): Promise<SavedIncentiveList> {
  const response = await axios.patch(
    `/admin/api/clients/${clientId}/incentive-lists/saved/${id}`,
    { name },
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
    },
  )
  return response.data
}

// 保存済みリスト一覧を取得
export async function fetchSavedIncentiveLists(
  clientId: number,
): Promise<SavedIncentiveList[]> {
  const response = await axios.get(
    `/admin/api/clients/${clientId}/incentive-lists/saved`,
    { headers: authHeaders() },
  )
  return response.data
}

// 保存済みリストをxlsxとしてダウンロード（Blob）
export async function downloadSavedIncentiveList(
  clientId: number,
  id: number,
): Promise<Blob> {
  const response = await axios.get(
    `/admin/api/clients/${clientId}/incentive-lists/saved/${id}/download`,
    { headers: authHeaders(), responseType: 'blob' },
  )
  return response.data as Blob
}

// 保存済みリストを論理削除
export async function deleteSavedIncentiveList(
  clientId: number,
  id: number,
): Promise<void> {
  await axios.delete(
    `/admin/api/clients/${clientId}/incentive-lists/saved/${id}`,
    { headers: authHeaders() },
  )
}
